// --- FILE: src/hooks/useShifts.js (FINAL - CORREGIDO Y USANDO UTILS) ---
import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { db } from '../services/firebase';
import { 
  collection, 
  addDoc, 
  updateDoc, 
  doc, // Importar doc
  query, 
  where, 
  getDocs, 
  getDoc, // Importar getDoc
  orderBy, 
  limit,
  Timestamp
} from 'firebase/firestore';
// Importar funciones de zona horaria y formateo desde utils
import { puertoRicoTimeToUtc, getCurrentPRTime, formatDuration as utilFormatDuration } from '../utils/formatters'; 


export const useShifts = () => {
  const { activeEmployee } = useAuth();
  
  const [activeShift, setActiveShift] = useState(null);
  const [shiftHistory, setShiftHistory] = useState([]);
  const [periodEarnings, setPeriodEarnings] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  // Función para obtener la tarifa por hora del empleado
  const getEmployeeHourlyRate = useCallback(async (employeeId) => {
    if (activeEmployee && activeEmployee.id === employeeId && activeEmployee.hourlyRate) {
      return activeEmployee.hourlyRate;
    }
    try {
      const docRef = doc(db, 'sellers', employeeId);
      const docSnap = await getDoc(docRef); // Corregido: usar getDoc en lugar de getDocs
      if (docSnap.exists()) {
        return docSnap.data().hourlyRate || 10.50;
      }
    } catch (err) {
      console.error("Error fetching employee hourly rate:", err);
    }
    return 10.50; // Fallback final
  }, [activeEmployee]);

  const fetchShiftHistory = useCallback(async (startDate, endDate) => {
    if (!activeEmployee) {
      setShiftHistory([]);
      setPeriodEarnings(0);
      return;
    }
    
    setLoading(true);
    setError('');
    
    try {
      const shiftsRef = collection(db, 'shifts');
      
      // Convertir las fechas de entrada (que ya son objetos Date interpretados en la zona local del input)
      // a UTC para la consulta de Firestore
      const startDateUtc = puertoRicoTimeToUtc(startDate);
      const endDateUtc = puertoRicoTimeToUtc(endDate);
      
      const q = query(
        shiftsRef,
        where('employeeId', '==', activeEmployee.id),
        where('startTime', '>=', Timestamp.fromDate(startDateUtc)),
        where('startTime', '<=', Timestamp.fromDate(endDateUtc)),
        orderBy('startTime', 'desc')
      );
      
      const querySnapshot = await getDocs(q);
      
      let totalEarnings = 0;
      const history = [];
      
      querySnapshot.docs.forEach(doc => {
        const data = doc.data();
        
        if (data.status === 'completed') {
          const earnings = data.earnings || 0;
          totalEarnings += earnings;
            
          history.push({ 
            id: doc.id, 
            ...data,
            earnings: parseFloat(earnings.toFixed(2))
          });
        }
      });
      
      setShiftHistory(history);
      setPeriodEarnings(parseFloat(totalEarnings.toFixed(2)));
      
    } catch (err) {
      console.error("Error buscando historial de turnos:", err);
      
      if (err.code === 'failed-precondition' && err.message.includes('index')) {
        setError("Los índices de la base de datos se están configurando. Intenta nuevamente en unos minutos.");
      } else {
        setError("No se pudo cargar el historial de turnos.");
      }
      
    } finally {
      setLoading(false);
    }
  }, [activeEmployee]); // Removido formatDuration, puertoRicoTimeToUtc que no se usaban directamente en este scope

  const checkForActiveShift = useCallback(async () => {
    if (!activeEmployee) {
      setActiveShift(null);
      return;
    }
    
    setLoading(true);
    setError('');
    
    try {
      const shiftsRef = collection(db, 'shifts');
      const q = query(
        shiftsRef, 
        where('employeeId', '==', activeEmployee.id),
        where('status', '==', 'active'),
        limit(1)
      );
      
      const querySnapshot = await getDocs(q);
      
      if (!querySnapshot.empty) {
        const activeShiftDoc = querySnapshot.docs[0];
        setActiveShift({ id: activeShiftDoc.id, ...activeShiftDoc.data() });
      } else {
        setActiveShift(null);
      }
      
    } catch (err) {
      console.error("Error buscando turno activo:", err);
      setError("No se pudo verificar el estado del turno.");
    } finally {
      setLoading(false);
    }
  }, [activeEmployee]);

  useEffect(() => {
    if (activeEmployee) {
      checkForActiveShift();
    } else {
      setActiveShift(null);
      setShiftHistory([]);
      setPeriodEarnings(0);
      setLoading(false);
    }
  }, [activeEmployee, checkForActiveShift]);

  const startShift = async () => {
    if (!activeEmployee || activeShift) return;
    
    setLoading(true);
    setError('');
    
    try {
      // Obtener la hora actual en Puerto Rico y convertir a UTC para Firestore
      const currentPRTime = getCurrentPRTime();
      const startTimeUtc = puertoRicoTimeToUtc(currentPRTime);

      const newShift = {
        employeeId: activeEmployee.id,
        employeeName: activeEmployee.name,
        startTime: Timestamp.fromDate(startTimeUtc),
        endTime: null,
        status: 'active',
        durationMinutes: 0,
        earnings: 0,
        createdAt: Timestamp.now()
      };
      
      const docRef = await addDoc(collection(db, 'shifts'), newShift);
      setActiveShift({ id: docRef.id, ...newShift });
      
    } catch (err) {
      console.error("Error iniciando turno:", err);
      setError("No se pudo iniciar el turno.");
    } finally {
      setLoading(false);
    }
  };

  const endShift = async () => {
    if (!activeShift) return;
    
    setLoading(true);
    setError('');
    
    try {
      // Obtener la hora actual en Puerto Rico y convertir a UTC para Firestore
      const currentPRTime = getCurrentPRTime();
      const endTimeUtc = puertoRicoTimeToUtc(currentPRTime);

      // Calcular duración en milisegundos y luego en minutos
      const startTimeMs = activeShift.startTime.toMillis();
      const endTimeMs = endTimeUtc.getTime();
      const durationMillis = endTimeMs - startTimeMs;
      const durationMinutes = Math.round(durationMillis / 60000);
      
      // Obtener la tarifa por hora del empleado activo
      const employeeHourlyRate = activeEmployee.hourlyRate || 10.50; // Usar activeEmployee.hourlyRate
      const calculatedEarnings = (durationMinutes / 60) * employeeHourlyRate;

      const shiftDocRef = doc(db, 'shifts', activeShift.id);
      await updateDoc(shiftDocRef, { 
        endTime: Timestamp.fromDate(endTimeUtc),
        status: 'completed',
        durationMinutes: durationMinutes,
        earnings: parseFloat(calculatedEarnings.toFixed(2)),
        updatedAt: Timestamp.now()
      });
      
      setActiveShift(null);
      
    } catch (err) {
      console.error("Error finalizando turno:", err);
      setError("No se pudo finalizar el turno.");
    } finally {
      setLoading(false);
    }
  };

  return { 
    activeShift, 
    isOnShift: !!activeShift, 
    shiftHistory, 
    periodEarnings, 
    loading, 
    error, 
    startShift, 
    endShift, 
    fetchShiftHistory,
    // NO exportar estas funciones desde aquí, ya están en utils/formatters.js
    // getCurrentPRTime, 
    // utcToPuertoRicoTime 
  };
};